import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IconButton, Typography, Box } from '@mui/material';
import Edit from '@mui/icons-material/Edit';

import { Table, PaginationProps } from '@frontend/features/common/components/Table/Table';
import { Link } from '@frontend/features/common/components/Link';
import { ChipCustom } from '@frontend/features/common/components/ChipCustom';
import { routes } from '@frontend/router/routes';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';

import { WorkflowResponseDto } from '@bandido/shared/dtos';

import { WorkflowModal } from './WorkflowModal';

interface WorkflowsTableProps {
  data?: WorkflowResponseDto[];
  pagination: PaginationProps;
}

export const WorkflowsTable = ({ data, pagination }: WorkflowsTableProps) => {
  const { t } = useTranslation();
  const { id: agencyId } = useParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Partial<WorkflowResponseDto>>();

  const handleModalVisibility = (isOpen: boolean) => () => setIsEditModalOpen(isOpen);

  const handleEditWorkflow = (workflow: Partial<WorkflowResponseDto>) => () => {
    setIsEditModalOpen(true);
    setSelectedWorkflow(workflow);
  };

  const items = data?.map((workflow) => ({
    name: { name: workflow.name, id: workflow.id },
    agency: workflow.agencyName,
    color: workflow.color,
    id: workflow.id,
    emailDomains: workflow.emailDomains,
  }));

  return (
    <>
      <Table
        height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
        columns={[
          {
            id: 'name',
            label: t('workflows.table.name'),
            align: 'left',
          },
          {
            id: 'color',
            label: t('workflows.table.color'),
            align: 'left',
          },
          {
            id: 'emailDomains',
            label: t('workflows.table.emailDomains'),
            align: 'left',
          },
          { id: 'actions', label: '', align: 'left' },
        ]}
        data={{
          key: 'id',
          items: items
            ? items.map((elItem) => ({
                ...elItem,
                name: (
                  <Link
                    to={`${routes.dashboard.agencyView.root.replace(
                      ':id',
                      String(agencyId),
                    )}/${routes.dashboard.agencyView.workflow.replace(':workflowId', elItem.id)}`}
                  >
                    {elItem.name.name}
                  </Link>
                ),
                color: (
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: elItem.color,
                      color: 'white',
                      borderRadius: 8,
                      textAlign: 'center',
                      width: 'fit-content',
                      padding: (theme) => theme.spacing(0.5, 1),
                    }}
                  >
                    {elItem.color}
                  </Typography>
                ),
                emailDomains: (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {Array.from({ length: Math.ceil(elItem.emailDomains.length / 3) }, (_, rowIndex) => (
                      <Box key={elItem.id} sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                        {elItem.emailDomains.slice(rowIndex * 3, (rowIndex + 1) * 3).map((domain, index) => (
                          <ChipCustom key={`${rowIndex}-${index}`} label={domain} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ))}
                  </Box>
                ),
                actions: (
                  <IconButton
                    onClick={handleEditWorkflow({
                      name: elItem.name.name,
                      id: elItem.name.id,
                      color: elItem.color,
                      agencyName: elItem.agency,
                      emailDomains: elItem.emailDomains,
                    })}
                  >
                    <Edit color="primary" />
                  </IconButton>
                ),
              }))
            : [],
          totalCount: data?.length || 0,
        }}
        pagination={pagination}
        stickyHeader
      />
      <WorkflowModal
        isEditMode
        data={selectedWorkflow}
        agencyId={String(agencyId)}
        open={isEditModalOpen}
        onClose={handleModalVisibility(false)}
      />
    </>
  );
};
